import { Action, Reducer } from "redux";
import { v4 as uuidv4 } from 'uuid';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from "../..";
import { RequestDataSupplierInfo, SurveyLocalStorageManager } from "../../Managers/SurveyLocalStorageManager";
import { ResultType } from "../../Models/Api/ApiResponse";
import { SimpleInventoryWithLinkedMaterialInfo } from "../../Models/Inventories/SimpleInventory";
import { InventoryOrCustomNameWithPossibleId } from "../../Models/InventoryNameWithPossibleId";
import { InviteSupplierModel, InviteSupplierRowModel, Supplier } from "../../Models/Supplier/Supplier";
import { ToxnotServiceClient } from "../../ToxnotServiceClient";

// ===============================================================================
//
// State Models
//

export interface SelectSuppliersMaterialsTableState {
    isSelectingSuppliersLoading: boolean;
    supplierRowModels: InviteSupplierRowModel[];
    supplierIdsToInventories: { [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] };
    existingSuppliers: Supplier[];
    existingSuppliersTable: { [supplierId: string]: Supplier };
    selectedSupplierRowIdentifier: string | null;
    materialsSelectorIsOpen: boolean;
    selectedSupplierRowIds: { [supplierId: string]: string };
    newMaterialsTextBoxValue: string;
}

// ===============================================================================
//
// Action Definitions
//

export const UpdateSelectSuppliersMaterialsTableState = "UPDATE_SELECT_SUPPLIERS_MATERIALS_TABLE_STATE";

interface UpdateSelectSuppliersMaterialsTableStateAction {
    type: typeof UpdateSelectSuppliersMaterialsTableState;
    value: Partial<SelectSuppliersMaterialsTableState>;
}

type KnownAction = UpdateSelectSuppliersMaterialsTableStateAction;

// ===============================================================================
//
// Helper Functions
//

const toxnotServiceClient = new ToxnotServiceClient();

interface AutofillReturnResponse {
    newSuppliersToInvite: InviteSupplierModel[];
    supplierIdsToInventories: { [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] };
}

const getInitialState = (): SelectSuppliersMaterialsTableState => {
    return {
        isSelectingSuppliersLoading: true,
        supplierRowModels: [],
        supplierIdsToInventories: {},
        existingSuppliers: [],
        existingSuppliersTable: {},
        selectedSupplierRowIdentifier: null,
        materialsSelectorIsOpen: false,
        selectedSupplierRowIds: {},
        newMaterialsTextBoxValue: ""
    };
}

// ===============================================================================

function verifyAndGetState(state: ApplicationState): SelectSuppliersMaterialsTableState {
    if (!state || !state.appServices || !state.selectSuppliersMaterialsTableState) {
        throw new Error("SelectSuppliersMaterialsTableState was not set up correctly.");
    }

    return state.selectSuppliersMaterialsTableState;
}

// ===============================================================================

/**
* First we go through the given supplier ids and create new invite supplier models
 * from them. Then we go through the existing supplier names. If a name matches an
 * existing supplier, we add that existing supplier to the invite supplier row models
 * 
 * Then we take all the supplier ids (the ones from local storage and the ones found
 * from name matching) and we fetch all the inventories for these suppliers. Then
 * we update the invite supplier row models with inventories for any provided inventory
 * ids that were given for a supplier in local storage.
 * 
 * Returns the all the supplier to invite models and the supplier ids to inventory ids.
 * If null is returned, an error occured.
 * 
 * NOTE: This method does not do any sort of state updates. That is supposed to be 
 * handled by the caller.
*/
async function getSuppliersToInviteForAutofill(
    state: ApplicationState,
    existingSuppliersTable: { [supplierId: string]: Supplier },
    requestDataSupplierInfo: RequestDataSupplierInfo,
): Promise<AutofillReturnResponse | null> {

    const supplierIds = Object.keys(requestDataSupplierInfo.existingSupplierIdsToInventoryIds);

    let newSuppliersToInvite: InviteSupplierModel[] = [];

    // Existing Suppliers
    for (let id of supplierIds) {
        newSuppliersToInvite.push({
            supplierId: id,
            supplierName: existingSuppliersTable[id].name,
            contactEmail: existingSuppliersTable[id].contactList[0]?.email ?? "",
            requestedMaterials: [] // leave blank for now, will fetch later.
        });
    }

    // New Suppliers
    for (let name of Object.keys(requestDataSupplierInfo.newSupplierNamesToEmail)) {
        let supplierIdToUse: string | null = null;

        const supplier = getExistingSupplierFromName(name, Object.values(existingSuppliersTable));

        // Matches an existing supplier name
        if (supplier !== null) {
            supplierIdToUse = supplier.supplierId;
            supplierIds.push(supplierIdToUse);
        }

        newSuppliersToInvite.push({
            supplierId: supplierIdToUse,
            supplierName: name,
            contactEmail: requestDataSupplierInfo.newSupplierNamesToEmail[name] ?? "",
            requestedMaterials: [],
        });
    }

    let supplierIdsToInventories: { [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] } = {};

    // If supplier ids were provided, fetch the supplier's inventories from the DB.
    if (supplierIds.length > 0) {
        const supplierInventoriesApiResult = await toxnotServiceClient.inventoriesGetForSupplierIds(supplierIds);

        // If there is an error simply return null...
        if (!supplierInventoriesApiResult.success || supplierInventoriesApiResult.data === null) {
            state.appServices!.displayApiErrorMessage();
            return null;
        }

        supplierIdsToInventories = supplierInventoriesApiResult.data;
    }

    // Helper method to get convert simple inventory to inventory used by request data.
    const getInventoryIdsToAutofillForSupplier = (supplierId: string): InventoryOrCustomNameWithPossibleId[] => {
        const inventoriesList = supplierIdsToInventories[supplierId];

        // Just return an empty list for suppliers that were not given inventory ids in local storage.
        if (!requestDataSupplierInfo.existingSupplierIdsToInventoryIds[supplierId]) {
            return [];
        }

        return inventoriesList
            .filter(inv => requestDataSupplierInfo.existingSupplierIdsToInventoryIds[supplierId]
                .includes(inv.inventoryId))
            .map(i => ({ name: i.name, inventoryId: i.inventoryId, customName: null }));
    }

    // Get the requested materials if any were provided.
    newSuppliersToInvite = newSuppliersToInvite.map(s => {
        if (s.supplierId !== null) {
            s = {
                ...s,
                requestedMaterials: getInventoryIdsToAutofillForSupplier(s.supplierId)
            }
        }

        return s;
    });

    return {
        newSuppliersToInvite: newSuppliersToInvite,
        supplierIdsToInventories: supplierIdsToInventories
    }
}

// ===============================================================================

/**
* Adds row identifers to the invite supplier models and turns them into invite supplier
* row models
*/
function addRowIdentifiersToSuppliersToInvite(suppliersToInvite: InviteSupplierModel[]): InviteSupplierRowModel[] {
    return suppliersToInvite.map(s => ({ ...s, rowIdentifier: uuidv4() }));
}

// ===============================================================================

/**
* Returns an empty supplier to invite row mode.
*/
function getEmptySupplierToInvite(): InviteSupplierRowModel {
    return {
        supplierName: "",
        supplierId: null,
        requestedMaterials: [],
        contactEmail: "",
        rowIdentifier: uuidv4()
    };
}

// ===============================================================================

/**
* Given a row identifier, this method returns the supplier in the table that corresponds
* to that id
*/
export function getSupplierFromRowIdentifier(rowIdentifier: string | null, state: SelectSuppliersMaterialsTableState) {
    if (rowIdentifier === null) {
        throw new Error("Cannot find model with null identifier");
    }

    const ret = state.supplierRowModels.find(s => s.rowIdentifier === rowIdentifier);

    if (!ret) {
        throw new Error("Unable to find supplier with rowIdentifier.");
    }

    return ret;
}

// ===============================================================================

/**
* Checks to see if a supplier names matches an existing supplier name. If it does,
* the existing supplier for that name is returned, else null. 
*/
export function getExistingSupplierFromName(supplierName: string, existingSuppliers: Supplier[]): Supplier | null {
    const index = existingSuppliers.findIndex(s => s.name.trim().toLowerCase() === supplierName.trim().toLowerCase());

    return index === -1 ? null : existingSuppliers[index];
}

// ===============================================================================

function updateSupplierToInvite(
    rowIdentifier: string,
    updatedSupplier: Partial<InviteSupplierModel>,
    state: SelectSuppliersMaterialsTableState,
    dispatch: (action: KnownAction) => void,
    removeSupplierId?: boolean
) {
    const newSuppliers = state.supplierRowModels.map(s => {
        if (s.rowIdentifier !== rowIdentifier) {
            return s;
        }

        return {
            ...s,
            ...updatedSupplier,
            supplierId: removeSupplierId ? null : (updatedSupplier?.supplierId ?? s.supplierId)
        }
    });

    dispatch({
        type: UpdateSelectSuppliersMaterialsTableState,
        value: {
            supplierRowModels: newSuppliers
        }
    });
}

// ===============================================================================

/**
* Gets any existing materials for a supplier.
*/
async function getInventoriesForSupplier(supplierId: string, getState: () => ApplicationState, dispatch: (action: KnownAction) => void) {
    const state = getState();

    const apiResult = await toxnotServiceClient.inventoriesGetForSupplier(supplierId);

    if (!apiResult.success || apiResult.data === null) {
        state.appServices!.displayApiErrorMessage();
        return;
    }

    const selectState = verifyAndGetState(state);

    const newSuppliersToInventories = { ...selectState.supplierIdsToInventories };
    newSuppliersToInventories[supplierId] = apiResult.data[supplierId];

    dispatch({
        type: UpdateSelectSuppliersMaterialsTableState,
        value: {
            supplierIdsToInventories: newSuppliersToInventories
        }
    });
}

// ===============================================================================

/**
* Called when an entered supplier names matches an existing supplier. This function
* inserts that existing supplier into the table. 
*/
async function addExistingSuppliersToSupplierToInvite(
    supplier: Supplier,
    rowId: string,
    getState: () => ApplicationState,
    dispatch: (action: KnownAction) => void,
    preserveRequestedMaterials?: boolean
) {
    const selectState = verifyAndGetState(getState());

    const supplierToInvite = getSupplierFromRowIdentifier(rowId, selectState);

    const newSupplierToInvite: InviteSupplierRowModel = {
        contactEmail: supplier.contactList[0]?.email ?? "",
        supplierId: supplier.supplierId,
        supplierName: supplier.name,
        requestedMaterials: preserveRequestedMaterials ? supplierToInvite.requestedMaterials : [],
        rowIdentifier: rowId
    };

    updateSupplierToInvite(rowId, newSupplierToInvite, selectState, dispatch);
    await getInventoriesForSupplier(supplier.supplierId, getState, dispatch);
}

// ===============================================================================

async function updateSupplierInventoriesForInitialSupplierModel(supplierIds: string[], state: ApplicationState, dispatch:  (action: KnownAction) => void) : Promise<{ [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] }> {

    let supplierIdToInventories: { [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] } = {};

    if (supplierIds.length > 0) {
        const supplierInventoriesApiResult = await toxnotServiceClient.inventoriesGetForSupplierIds(supplierIds);

        // If there is an error simply return an empty list...
        if (!supplierInventoriesApiResult.success || supplierInventoriesApiResult.data === null) {
            state.appServices!.displayApiErrorMessage();
            return {};
        }

        // Update the local state with the supplier ids and inventories
        supplierIdToInventories = supplierInventoriesApiResult.data;
    }

    return supplierIdToInventories;

}

// ===============================================================================
//
// Action Creators
//

export const actionCreators = {
    initializeSelectSuppliers: (initialSupplierRowModels?: InviteSupplierModel[]): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        verifyAndGetState(state);

        const apiResult = await toxnotServiceClient.suppliersGet();

        if (!apiResult.success || apiResult.data === null) {
            state.appServices!.displayApiErrorMessage();
            return;
        }

        const existingSuppliersTable: { [supplierId: string]: Supplier } = {};

        for (let s of apiResult.data) {
            existingSuppliersTable[s.supplierId] = s;
        }

        let supplierRowModelsToUse: InviteSupplierRowModel[] = state.selectSuppliersMaterialsTableState!.supplierRowModels;

        let supplierIdsToInventoriesToUse: { [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] } = state.selectSuppliersMaterialsTableState!.supplierIdsToInventories;

        const manager = new SurveyLocalStorageManager(state.appServices!.userContext!.userId);
        const requestDataSupplierInfo = manager.getAndClearRequestDataSupplierInfo();

        if (initialSupplierRowModels) {
            supplierRowModelsToUse = addRowIdentifiersToSuppliersToInvite(initialSupplierRowModels);
            supplierIdsToInventoriesToUse = await updateSupplierInventoriesForInitialSupplierModel(initialSupplierRowModels.filter(v => v.supplierId !== null).map(v => v.supplierId!), state, dispatch);
        } 
        else if (requestDataSupplierInfo !== null && Object.keys(requestDataSupplierInfo).length > 0) {
            const response = await getSuppliersToInviteForAutofill(state, existingSuppliersTable, requestDataSupplierInfo);

            if (response !== null) {
                supplierRowModelsToUse = addRowIdentifiersToSuppliersToInvite(response.newSuppliersToInvite);
                supplierIdsToInventoriesToUse = response.supplierIdsToInventories;
            }
        }

        if (supplierRowModelsToUse.length === 0){
            supplierRowModelsToUse.push(getEmptySupplierToInvite());
        }

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                isSelectingSuppliersLoading: false,
                supplierRowModels: supplierRowModelsToUse,
                existingSuppliers: apiResult.data,
                existingSuppliersTable: existingSuppliersTable,
                supplierIdsToInventories: supplierIdsToInventoriesToUse
            }
        });
    },
    updateSelectSuppliersState: (updatedState: Partial<SelectSuppliersMaterialsTableState>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                ...state,
                ...updatedState
            }
        });
    },
    updateSupplierToInvite: (rowIdentifier: string, updatedSupplier: Partial<InviteSupplierModel>, removeSupplierId?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        updateSupplierToInvite(rowIdentifier, updatedSupplier, state, dispatch, removeSupplierId);
    },
    addSupplierToInvite: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        const newSupplier = getEmptySupplierToInvite();

        const newSuppliers = [...state.supplierRowModels];
        newSuppliers.push(newSupplier);

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers
            }
        });
    },
    importSuppliers: (e: React.ChangeEvent<HTMLInputElement>): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();
        let selectState = verifyAndGetState(appState);

        if ((e.target.files === null) || (e.target.files.length === 0)) {
            return;
        }

        const file = e.target.files[0];

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                isSelectingSuppliersLoading: true
            }
        });

        // Clear the file so another import is possible.
        e.target.value = '';

        const apiResult = await toxnotServiceClient.importSuppliersFromFile(file);

        if (apiResult.result === ResultType.UploadTooLarge) {
            appState.appServices!.displayApiErrorMessage(appState.appServices!.localizeString("Sorry, your current account does not let you upload files of that size."));
        }

        // If there is an issue, stil allow the user to enter in new suppliers.
        if (!apiResult.success || apiResult.data === null) {
            appState.appServices!.displayApiErrorMessage();

            dispatch({
                type: UpdateSelectSuppliersMaterialsTableState,
                value: {
                    isSelectingSuppliersLoading: false
                }
            });

            return;
        }

        let newSuppliers = [...selectState.supplierRowModels];
        newSuppliers = newSuppliers.concat(addRowIdentifiersToSuppliersToInvite(apiResult.data));

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers
            }
        });

        // Check to see if any imported suppliers match existing suppliers.
        for (let s of newSuppliers) {
            const existingSupplier = getExistingSupplierFromName(s.supplierName, selectState.existingSuppliers);

            if (existingSupplier === null) {
                continue;
            }

            // Await this to avoid race conditons with the state update.
            await addExistingSuppliersToSupplierToInvite(existingSupplier, s.rowIdentifier, getState, dispatch, true);
        }

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                isSelectingSuppliersLoading: false
            }
        });
    },
    removeSuppliersToInvite: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        const selectState = verifyAndGetState(appState);

        const supplierIdsToRemove = Object.keys(selectState.selectedSupplierRowIds);

        if (supplierIdsToRemove.length === 0) {
            alert(appState.appServices!.localizeString("Please select at least one supplier below to delete."))
            return;
        }

        let newSuppliers = [...selectState.supplierRowModels];

        newSuppliers = newSuppliers.filter(s => !supplierIdsToRemove.includes(s.rowIdentifier));

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers,
                selectedSupplierRowIds: {}
            }
        });
    },
    addRequestedMaterial: (materialName: string, supplier: InviteSupplierRowModel): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        if (materialName.trim() === "") {
            return;
        }

        const newRequestedMaterials = [...supplier.requestedMaterials];

        // Don't allow duplicate names.
        if (!newRequestedMaterials.some(r => r.name.trim() === materialName.trim())) {
            newRequestedMaterials.push({ name: materialName, inventoryId: null, customName: null });

            updateSupplierToInvite(supplier.rowIdentifier, { requestedMaterials: newRequestedMaterials }, state, dispatch);
        }
    },
    removeRequestedMaterial: (rowIdentifier: string, materialToRemove: InventoryOrCustomNameWithPossibleId): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        const supplierToUpdate = getSupplierFromRowIdentifier(rowIdentifier, state);

        const newSuppliers = state.supplierRowModels.map(s => {
            if (s.rowIdentifier !== supplierToUpdate.rowIdentifier) {
                return s;
            }

            // If its an existing material, remove via id
            if (materialToRemove.inventoryId !== null) {
                s.requestedMaterials = s.requestedMaterials.filter(r => r.inventoryId !== materialToRemove.inventoryId);
            }
            // remove by name
            else {
                s.requestedMaterials = s.requestedMaterials.filter(r => r.name.trim() !== materialToRemove.name.trim());
            }

            return s;
        });

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers,
            }
        });
    },
    updateSelectedSuppliers: (rowIds: { [id: string]: string }): AppThunkAction<KnownAction> => (dispatch, getState) => {
        verifyAndGetState(getState());

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                selectedSupplierRowIds: {...rowIds},
            }
        });
    },
    addExistingSuppliersToSupplierToInvite: (rowId: string, supplier: Supplier, preserveMaterials?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        addExistingSuppliersToSupplierToInvite(supplier, rowId, getState, dispatch, preserveMaterials)
    },
    inventoriesSelected: (selectedInventoryIds: { [id: string]: string }, inventoriesToRender: SimpleInventoryWithLinkedMaterialInfo[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        const allInventoriesSelected = inventoriesToRender.filter(i => selectedInventoryIds[i.inventoryId]);

        const supplierToUpdateWithNewMaterials = state.supplierRowModels.find(s => s.rowIdentifier === state.selectedSupplierRowIdentifier);

        if (!supplierToUpdateWithNewMaterials) {
            throw new Error("Unable to find supplier to update.")
        }

        const getCustomNameFromSupplier = (inventoryId: string) => {
            return supplierToUpdateWithNewMaterials.requestedMaterials.find(r => r.inventoryId === inventoryId)?.customName ?? null;
        }

        const newRequestedMaterialsToAdd: InventoryOrCustomNameWithPossibleId[] = allInventoriesSelected.map(i => ({ inventoryId: i.inventoryId, name: i.name, customName: getCustomNameFromSupplier(i.inventoryId) }));

        const newSuppliers = state.supplierRowModels.map(s => {
            if (s.rowIdentifier !== state.selectedSupplierRowIdentifier) {
                return s;
            }
            // Remove all the inventories that have an inventory id and replace with new inventories. 
            s.requestedMaterials = s.requestedMaterials.filter(r => r.inventoryId === null).concat(newRequestedMaterialsToAdd);

            return s;
        });

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers
            }
        });
    },
    updateSupplierRowModelsFromSurveyEditor: (model: InviteSupplierRowModel[]) : KnownAction => ({
        type: UpdateSelectSuppliersMaterialsTableState,
        value: {
            supplierRowModels: model
        }
    }),
    setCustomName: (rowIdentifierId: string, customName: string, materialIndex: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().selectSuppliersMaterialsTableState;

        if (!state) {
            return;
        }

        const newSupplierModels = [...state.supplierRowModels];

        const newSupplierModel = newSupplierModels.find(s => s.rowIdentifier === rowIdentifierId);

        if (!newSupplierModel) {
            return;
        }

        newSupplierModel.requestedMaterials.orderBy(m => m.name)[materialIndex].customName = customName;

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSupplierModels
            }
        });
    },
    setCustomNameForAllMaterials: (columnId: string, models: InviteSupplierRowModel[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState();
        
        const supplierRowModels = models.map((suppliers, i1) => {
            suppliers.requestedMaterials = suppliers.requestedMaterials.map((v, i) => {
                v.customName = v.inventoryId ? state.createAndEditSurveysState!.surveyMetadata.inventoryIdToSummaryRowContentsMap[v.inventoryId]?.rowSummaryContent.find((v, i) => v.columnId == columnId)?.value ?? null : null;
                return v
            })
            return suppliers;
        });

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: supplierRowModels
            }
        });

    },
    clearSelectSuppliersState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: getInitialState()
        });
    },
    selectAllMaterialsWithInventoryIdsForSelectedSuppliers: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = verifyAndGetState(getState());

        const newSuppliers = state.supplierRowModels.map((v, _) => {
            if (v.supplierId === null) return v;

            v.requestedMaterials = v.requestedMaterials.filter(r => r.inventoryId === null).concat(state.supplierIdsToInventories[v.supplierId].map(i => ({ inventoryId: i.inventoryId, name: i.name, customName: null })));
            return v;
        })

        dispatch({
            type: UpdateSelectSuppliersMaterialsTableState,
            value: {
                supplierRowModels: newSuppliers
            }
        })

    },
};

// ===============================================================================
//
// Reducers
//

export const reducer: Reducer<SelectSuppliersMaterialsTableState> = (state: SelectSuppliersMaterialsTableState | undefined, action: Action): SelectSuppliersMaterialsTableState => {
    if (state === undefined) {
        return getInitialState();
    }

    switch (action.type) {
        case UpdateSelectSuppliersMaterialsTableState:
            return {
                ...state,
                ...(action as UpdateSelectSuppliersMaterialsTableStateAction).value
            };
    }

    return state;
}